import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { Panel } from '@/components/platform/AsyncPanel';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import type {
  ConnectionSecretKind,
  ConnectionSecretStatus,
  CredentialTestResult,
} from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import styles from '@/pages/platform/platform-pages.module.css';

/**
 * Where an operator hands CampusOne the credentials the far end issued.
 *
 * THE VALUES GO IN AND NEVER COME BACK. The API returns `configured`, a twelve-character
 * fingerprint, and who set it when — never the secret. So this panel has no "reveal"
 * affordance and no populated input: an empty field over a configured credential means
 * "leave it alone", not "it is missing", and the status line beside it says which.
 *
 * The fingerprint is the whole reason this is usable. The Switch's partner console shows
 * a credential once; an operator pastes it here and can then confirm the two match
 * without either screen displaying the value again.
 *
 * TESTING IS SEPARATE FROM HEALTH, AND THE DISTINCTION IS LOAD-BEARING. The health check
 * classifies 401 and 403 as reachable — right for connectivity — so a connection with a
 * wrong secret sits green. Only this test signs a real request and reports what the far
 * end thought of it. INSUFFICIENT_SCOPE is rendered as a PASS, because it means the
 * signature verified and only the permission check declined.
 */

const KINDS: readonly ConnectionSecretKind[] = ['API_KEY', 'HMAC_SECRET', 'WEBHOOK_SECRET'];

/** Tone per outcome. INSUFFICIENT_SCOPE is deliberately grouped with success. */
const OUTCOME_TONE: Record<CredentialTestResult['outcome'], 'ok' | 'bad' | 'muted'> = {
  ACCEPTED: 'ok',
  INSUFFICIENT_SCOPE: 'ok',
  REJECTED: 'bad',
  UNREACHABLE: 'muted',
  NOT_CONFIGURED: 'muted',
};

export function ConnectionCredentialsPanel({ connectionId }: { connectionId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const fetcher = useCallback(
    () => platformApi.connections.secrets(connectionId),
    [connectionId],
  );
  const { state, reload } = useAsyncData<ConnectionSecretStatus[]>(
    `connection-secrets:${connectionId}`,
    fetcher,
    connectionId.length > 0,
  );

  const [draft, setDraft] = useState<Partial<Record<ConnectionSecretKind, string>>>({});
  const [saving, setSaving] = useState<ConnectionSecretKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<CredentialTestResult | null>(null);

  const save = async (kind: ConnectionSecretKind) => {
    const value = (draft[kind] ?? '').trim();
    if (!value) return;

    setSaving(kind);
    setError(null);
    try {
      await platformApi.connections.putSecret(connectionId, { kind, value });
      // Cleared immediately on success. Leaving a secret sitting in a React state tree —
      // and therefore in a devtools inspector and any error report that serialises props
      // — undoes most of what storing it encrypted was for.
      setDraft((current) => ({ ...current, [kind]: '' }));
      // The stale result belongs to the previous credential and would read as a verdict
      // on the one just saved.
      setResult(null);
      reload();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSaving(null);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setError(null);
    setResult(null);
    try {
      setResult(await platformApi.connections.credentialTest(connectionId));
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setTesting(false);
    }
  };

  const statuses = state.status === 'ready' ? state.data : [];
  const statusFor = (kind: ConnectionSecretKind) => statuses.find((s) => s.kind === kind);

  return (
    <Panel title={t('platform.connections.credentials.title')} icon="integration">
      <p className={styles.rowDetail}>{t('platform.connections.credentials.intro')}</p>

      <div className={styles.rowList}>
        {KINDS.map((kind) => {
          const status = statusFor(kind);
          const configured = status?.configured ?? false;

          return (
            <div className={styles.row} key={kind}>
              <div className={styles.rowMain}>
                <p className={styles.rowTitle}>{t(`platform.connections.credentials.kind.${kind}`)}</p>
                <p className={styles.rowDetail}>
                  {t(`platform.connections.credentials.hint.${kind}`)}
                </p>

                <p className={styles.rowMetaLine}>
                  {configured ? (
                    <>
                      <Icon name="check" size={13} />
                      {/*
                        The fingerprint, not the value. Compare it against what the issuing
                        console showed to confirm the two halves match.
                      */}
                      {t('platform.connections.credentials.configured', {
                        fingerprint: status?.fingerprint ?? '—',
                      })}
                      {status?.updatedAt && ` · ${formatDateTime(status.updatedAt, locale)}`}
                    </>
                  ) : (
                    t('platform.connections.credentials.absent')
                  )}
                </p>

                <div className={styles.dialogRow}>
                  <input
                    className={styles.input}
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    /* Empty over a configured credential means "leave it"; the status line
                       above says whether one is set. A populated field would imply the
                       value had been read back, which never happens. */
                    placeholder={t(
                      configured
                        ? 'platform.connections.credentials.replacePlaceholder'
                        : 'platform.connections.credentials.pastePlaceholder',
                    )}
                    value={draft[kind] ?? ''}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [kind]: event.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className={styles.primaryBtnSm}
                    disabled={saving === kind || !(draft[kind] ?? '').trim()}
                    onClick={() => void save(kind)}
                  >
                    {saving === kind
                      ? t('platform.connections.credentials.saving')
                      : t(
                          configured
                            ? 'platform.connections.credentials.rotate'
                            : 'platform.connections.credentials.store',
                        )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p className={styles.formError} role="alert">
          {error}
        </p>
      )}

      <div className={styles.transitionRow}>
        <button
          type="button"
          className={styles.secondaryBtn}
          disabled={testing}
          onClick={() => void runTest()}
        >
          <Icon name="integration" size={14} />
          {testing
            ? t('platform.connections.credentials.testing')
            : t('platform.connections.credentials.test')}
        </button>
      </div>

      {result && (
        <div
          className={
            OUTCOME_TONE[result.outcome] === 'bad' ? styles.warningNotice : styles.dataNotice
          }
          role="status"
        >
          <p className={styles.warningTitle}>
            {t(`platform.connections.credentials.outcome.${result.outcome}`)}
          </p>
          <p className={styles.warningBody}>{result.detail}</p>
          <p className={styles.rowMetaLine}>
            {t('platform.connections.credentials.source', { source: result.credentialSource })}
            {result.httpStatus !== null && ` · HTTP ${result.httpStatus}`}
            {result.responseTimeMs !== null && ` · ${result.responseTimeMs}ms`}
          </p>
        </div>
      )}
    </Panel>
  );
}
